import {
  MARKET_METHODOLOGY_VERSION,
  type MarketAnalysisComparable,
  type MarketConfidence,
  type MarketDatasetSummary,
  type MarketStrategy,
  type MarketSubjectSnapshot,
  type MarketValuationResult,
  type OwnerPricePosition
} from "../domain/types.ts";
import { normalizeLocationText } from "../location/locationSearch.ts";
import { publicMarketIdentityNamespace } from "../data/publicMarketTypes.ts";
import { effectiveApartmentArea } from "../domain/propertyMatching.ts";
import { isAutomaticComparable } from "../domain/comparableLifecycle.ts";
import { qualifyMarketComparable } from "../domain/selectionPolicy.ts";

type PricePoint = {
  pricePerM2: number;
  weight: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const valuationIdentityKeys = (comparable: MarketAnalysisComparable) => {
  const sources = Array.isArray(comparable.sourceSnapshot?.sources) ? comparable.sourceSnapshot.sources : [];
  const identities = sources.flatMap((value) => {
    if (!isRecord(value) || typeof value.providerId !== "string" || typeof value.externalId !== "string") return [];
    const namespace = typeof value.identityNamespace === "string"
      ? value.identityNamespace
      : publicMarketIdentityNamespace(value.providerId);
    return [`identity:${namespace}:${value.externalId}`];
  });
  if (identities.length > 0) return [...new Set(identities)];
  if (comparable.sourceUrl) return [`url:${comparable.sourceUrl}`];
  return comparable.sourceHash ? [`hash:${comparable.sourceHash}`] : [];
};

const plausibleArea = (subject: MarketSubjectSnapshot, area: number | undefined) => {
  if (!area || !Number.isFinite(area)) return false;
  const propertyType = normalizeLocationText(subject.propertyType);
  if (propertyType.includes("byt")) return area >= 10 && area <= 500;
  if (propertyType.includes("garaz")) return area >= 5 && area <= 500;
  if (propertyType.includes("dom")) return area >= 15 && area <= 5_000;
  return area >= 1 && area <= 1_000_000;
};

const plausiblePrice = (price: number | undefined) =>
  Boolean(price && Number.isFinite(price) && price >= 1_000 && price <= 100_000_000);

const plausiblePricePerM2 = (price: number | undefined, area: number | undefined) => {
  if (!plausiblePrice(price) || !area) return false;
  const derived = price! / area;
  return Number.isFinite(derived) && derived >= 1 && derived <= 200_000;
};

const valuationSimilarityScore = (comparable: MarketAnalysisComparable) =>
  comparable.scoreBreakdown.valuationWeightScore ?? comparable.similarityScore;

export const prepareValuationComparables = (
  subject: MarketSubjectSnapshot,
  comparables: MarketAnalysisComparable[]
) => {
  const seenIdentities = new Set<string>();
  return [...comparables]
    .sort((first, second) => first.sortOrder - second.sortOrder || first.id.localeCompare(second.id))
    .filter((comparable) => {
      if (comparable.decision === "excluded") return false;
      if (comparable.marketRole === "competition") return false;
      if (isAutomaticComparable(comparable) && !qualifyMarketComparable(comparable.scoreBreakdown).qualified) return false;
      const area = effectiveApartmentArea(comparable).value;
      if (!plausibleArea(subject, area)) return false;
      if (!plausiblePricePerM2(comparable.askingPrice, area)
        && !plausiblePricePerM2(comparable.realizedPrice, area)) return false;
      const identities = valuationIdentityKeys(comparable);
      if (identities.some((identity) => seenIdentities.has(identity))) return false;
      identities.forEach((identity) => seenIdentities.add(identity));
      return true;
    });
};

const median = (values: number[]) => {
  const sorted = [...values].sort((first, second) => first - second);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

const quantile = (values: number[], percentile: number) => {
  const sorted = [...values].sort((first, second) => first - second);
  if (sorted.length === 1) return sorted[0];
  const index = (sorted.length - 1) * percentile;
  const lower = Math.floor(index);
  const fraction = index - lower;
  return sorted[lower + 1] === undefined
    ? sorted[lower]
    : sorted[lower] + fraction * (sorted[lower + 1] - sorted[lower]);
};

const withoutOutliers = (points: PricePoint[]) => {
  if (points.length < 4) return points;
  const values = points.map((point) => point.pricePerM2);
  const center = median(values);
  const mad = median(values.map((value) => Math.abs(value - center)));
  if (mad === 0) return points;
  const limit = 3 * 1.4826 * mad;
  const lowerQuartile = quantile(values, 0.25);
  const upperQuartile = quantile(values, 0.75);
  const iqr = upperQuartile - lowerQuartile;
  const filtered = points.filter((point) =>
    Math.abs(point.pricePerM2 - center) <= limit &&
    (iqr === 0 || (point.pricePerM2 >= lowerQuartile - 1.5 * iqr && point.pricePerM2 <= upperQuartile + 1.5 * iqr))
  );
  return filtered.length > 0 ? filtered : points;
};

const weightedMedian = (points: PricePoint[]) => {
  const sorted = [...points].sort((first, second) => first.pricePerM2 - second.pricePerM2);
  const totalWeight = sorted.reduce((sum, point) => sum + point.weight, 0);
  let cumulative = 0;
  for (const point of sorted) {
    cumulative += point.weight;
    if (cumulative >= totalWeight / 2) return point.pricePerM2;
  }
  return sorted[sorted.length - 1]?.pricePerM2 ?? 0;
};

const pointsFor = (comparables: MarketAnalysisComparable[], priceKind: "asking" | "realized") =>
  withoutOutliers(comparables.flatMap((comparable) => {
    const price = priceKind === "asking" ? comparable.askingPrice : comparable.realizedPrice;
    const area = effectiveApartmentArea(comparable).value;
    if (!plausiblePricePerM2(price, area)) return [];
    return [{
      pricePerM2: price! / area!,
      weight: Math.max(0.1, valuationSimilarityScore(comparable) / 100)
    }];
  }));

const summarizeMarket = (
  comparables: MarketAnalysisComparable[],
  priceKind: "asking" | "realized",
  subjectArea: number
): MarketDatasetSummary | undefined => {
  const points = pointsFor(comparables, priceKind);
  if (points.length === 0) return undefined;
  const values = points.map((point) => point.pricePerM2);
  return {
    count: points.length,
    medianPricePerM2: Math.round(median(values)),
    weightedPricePerM2: Math.round(weightedMedian(points)),
    low: Math.round(quantile(values, 0.25) * subjectArea),
    high: Math.round(quantile(values, 0.75) * subjectArea),
    dispersion: median(values) > 0
      ? Math.round(((quantile(values, 0.75) - quantile(values, 0.25)) / median(values)) * 1000) / 10
      : 100
  };
};

export const commercialRoundPrice = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const step = value < 250_000 ? 1_000 : value < 1_000_000 ? 5_000 : 10_000;
  return Math.max(0, Math.ceil(value / step) * step - 100);
};

const ownerPosition = (ownerPrice: number | undefined, low: number, high: number): OwnerPricePosition => {
  if (!ownerPrice || low <= 0 || high <= 0) return "unknown";
  if (ownerPrice < low) return "below";
  if (ownerPrice > high) return "above";
  return "within";
};

const confidenceFor = (
  comparables: MarketAnalysisComparable[],
  selectedMarket: MarketDatasetSummary,
  calculatedAt: string
): { score: number; label: MarketConfidence } => {
  const relevant = comparables.filter((comparable) => comparable.askingPrice || comparable.realizedPrice);
  const averageSimilarity = relevant.length > 0
    ? relevant.reduce((sum, comparable) => sum + valuationSimilarityScore(comparable), 0) / relevant.length
    : 0;
  const averageCompleteness = relevant.length > 0
    ? relevant.reduce((sum, comparable) => sum + comparable.scoreBreakdown.completeness, 0) / relevant.length
    : 0;
  const calculationTime = Date.parse(calculatedAt);
  const freshCount = relevant.filter((comparable) => calculationTime - Date.parse(comparable.capturedAt) <= 180 * 86_400_000).length;
  const realizedCount = relevant.filter((comparable) => comparable.realizedPrice).length;
  const countScore = Math.min(1, selectedMarket.count / 6) * 25;
  const similarityScore = Math.min(1, averageSimilarity / 80) * 25;
  const completenessScore = averageCompleteness * 15;
  const freshnessScore = (freshCount / Math.max(1, relevant.length)) * 12;
  const dispersionScore = Math.max(0, 1 - selectedMarket.dispersion / 40) * 13;
  const realizedScore = Math.min(1, realizedCount / 3) * 10;
  const score = Math.round(Math.max(0, Math.min(100, countScore + similarityScore + completenessScore + freshnessScore + dispersionScore + realizedScore)));
  return { score, label: score >= 75 ? "high" : score >= 48 ? "medium" : "low" };
};

export const calculateValuation = (
  subject: MarketSubjectSnapshot,
  comparables: MarketAnalysisComparable[],
  calculatedAt = new Date().toISOString()
): MarketValuationResult => {
  const subjectArea = effectiveApartmentArea(subject).value ?? 0;
  const validatedComparables = prepareValuationComparables(subject, comparables);
  const askingMarket = summarizeMarket(validatedComparables, "asking", subjectArea);
  const realizedMarket = summarizeMarket(validatedComparables, "realized", subjectArea);
  const selectedMarket = realizedMarket && realizedMarket.count >= 2 ? realizedMarket : askingMarket ?? realizedMarket;

  if (!selectedMarket || subjectArea <= 0) {
    return {
      askingMarket,
      realizedMarket,
      recommendedListingPrice: 0,
      marketLow: 0,
      marketHigh: 0,
      marketMid: 0,
      ownerPricePosition: "unknown",
      confidenceScore: 0,
      confidence: "low",
      methodologyVersion: MARKET_METHODOLOGY_VERSION,
      calculatedAt
    };
  }

  const estimatedValue = selectedMarket.weightedPricePerM2 * subjectArea;
  const listingBuffer = selectedMarket === realizedMarket ? 1.03 : 1;
  const recommendedListingPrice = commercialRoundPrice(estimatedValue * listingBuffer);
  const marketLow = commercialRoundPrice(selectedMarket.low);
  const marketHigh = commercialRoundPrice(Math.max(selectedMarket.high, selectedMarket.low));
  const marketMid = commercialRoundPrice((selectedMarket.low + selectedMarket.high) / 2);
  const ownerReference = subject.askingPriceAtAnalysis && subject.askingPriceAtAnalysis > marketHigh ? marketHigh : marketMid;
  const ownerDifferenceAmount = subject.askingPriceAtAnalysis && ownerReference > 0
    ? Math.round(subject.askingPriceAtAnalysis - ownerReference)
    : undefined;
  const ownerDifferencePercent = ownerDifferenceAmount !== undefined && ownerReference > 0
    ? Math.round((ownerDifferenceAmount / ownerReference) * 1000) / 10
    : undefined;
  const confidence = confidenceFor(validatedComparables, selectedMarket, calculatedAt);

  return {
    askingMarket,
    realizedMarket,
    recommendedListingPrice,
    marketLow,
    marketHigh,
    marketMid,
    ownerPricePosition: ownerPosition(subject.askingPriceAtAnalysis, marketLow, marketHigh),
    ownerDifferencePercent,
    ownerDifferenceAmount,
    confidenceScore: confidence.score,
    confidence: confidence.label,
    methodologyVersion: MARKET_METHODOLOGY_VERSION,
    calculatedAt
  };
};

export const buildMarketStrategies = (result: MarketValuationResult): MarketStrategy[] => [
  {
    kind: "quick-sale",
    title: "RÝCHLY PREDAJ",
    listingPrice: commercialRoundPrice(result.marketLow * 0.99),
    explanation: "Cena pri spodnej hrane vypočítaného pásma podporí skorú reakciu relevantných záujemcov."
  },
  {
    kind: "recommended",
    title: "ODPORÚČANÁ STRATÉGIA",
    listingPrice: result.recommendedListingPrice,
    explanation: "Cena vychádza z robustného odhadu najpodobnejších ponúk a dostupných realizovaných cien."
  },
  {
    kind: "market-test",
    title: "TEST TRHU",
    listingPrice: commercialRoundPrice(Math.max(result.marketHigh, result.recommendedListingPrice * 1.03)),
    explanation: "Vyššia štartovacia cena overí ochotu trhu, no vyžaduje vopred dohodnutý termín vyhodnotenia reakcií."
  }
];
